import { useTranslation } from "react-i18next";
import { Divider, Form, Icon, Modal } from "semantic-ui-react";

import { createFormHelper } from "lib/Form";
import { isBrandNameCountryTaken } from "modules/BrandsDashboard/components/BrandModal";
import { BrandSettingsFields } from "modules/BrandsDashboard/components/BrandSettingsFields";
import { useBrandGroupsQuery, useBrandsQuery } from "modules/BrandsDashboard/hooks";

import type { BrandFormPayload } from "modules/BrandsDashboard/components/BrandModal/hooks";
import type { BrandSettingsFormValues } from "modules/BrandsDashboard/components/BrandSettingsFields";

export type BrandFormValues = Omit<BrandFormPayload, "brand_group_id"> & BrandSettingsFormValues & { brand_group_id?: number };

type BrandFormProps = {
  isEdit: boolean;
  isPending: boolean;
  onClose: () => void;
};

const FormHelper = createFormHelper<BrandFormValues>();

export const BrandForm = ({ isEdit, isPending, onClose }: BrandFormProps) => {
  const { t } = useTranslation();
  const { watch, formState } = FormHelper.useFormContext();

  const { data: brands = [] } = useBrandsQuery();
  const { data: brandGroups = [] } = useBrandGroupsQuery();
  const brandGroupOptions = brandGroups.map(({ id, name }) => ({ key: id, value: id, text: name }));

  const [id, name, country, vinPrefix, brandGroupId] = watch(["id", "name", "country", "vin_prefix", "brand_group_id"]);

  const isBrandTaken = isBrandNameCountryTaken(brands, { id, name, country });

  return (
    <>
      <Modal.Header className="padding-container">
        <div className="header-container ">
          <div className="header-title">{isEdit ? t("v8_edit_brand", "Edit brand") : t("v8_add_brand", "Add brand")}</div>
          <FormHelper.Field.Checkbox name="is_active" label={t("v8_active", "Active")} />
        </div>
      </Modal.Header>
      <Divider fitted />

      <Modal.Content className="padding-container" scrolling>
        <Form.Group widths="equal">
          <Form.Field required error={formState.isSubmitted && (!name.trim() || isBrandTaken)}>
            <label>{t("v8_brand", "Brand")}</label>
            <FormHelper.Field.Input name="name" inputProps={{ placeholder: t("v8_brand", "Brand") }} />
            {formState.isSubmitted && isBrandTaken && (
              <div className="field-error">{t("v8_brand_already_exists_for_this_country", "Brand already exists for this country")}</div>
            )}
          </Form.Field>
          <Form.Field required error={formState.isSubmitted && !vinPrefix}>
            <label>{t("v8_vin_number", "Vin #")}</label>
            <FormHelper.Field.Input name="vin_prefix" inputProps={{ placeholder: t("v8_vin_number", "Vin #") }} />
          </Form.Field>
        </Form.Group>
        <Form.Group widths="equal">
          {isEdit && (
            <Form.Field required error={formState.isSubmitted && !brandGroupId}>
              <label>{t("v8_group", "Group")}</label>
              <FormHelper.Field.Dropdown
                name="brand_group_id"
                options={brandGroupOptions}
                dropdownProps={{ search: true, clearable: true, placeholder: t("v8_group", "Group") }}
              />
            </Form.Field>
          )}
          <Form.Field>
            <label>{t("v8_pon_brand_code", "PON Brand Code")}</label>
            <FormHelper.Field.Input name="pon_brand_code" inputProps={{ placeholder: t("v8_pon_brand_code", "PON Brand Code") }} />
          </Form.Field>
        </Form.Group>

        <BrandSettingsFields />
      </Modal.Content>
      <Divider fitted />

      <Modal.Actions className="padding-container">
        <div className="action-buttons-container">
          <div className="right-buttons">
            <Form.Button type="button" className="light" onClick={onClose}>
              {t("v8_cancel", "Cancel")}
              <Icon className="xmark" />
            </Form.Button>
            <Form.Button type="submit" color="green" disabled={isPending} loading={isPending}>
              {t("v8_save", "Save")}
              <Icon className="check" />
            </Form.Button>
          </div>
        </div>
      </Modal.Actions>
    </>
  );
};
